import {
  Course,
  CourseAttempt,
  CourseStatusAttemptEnum,
} from '@/client/courses';
import {
  LearnerCourseDispatchResponse,
  ScormVersion,
} from '@/client/learner-course';
import { User } from '@/client/users';
import {
  ContentFrameIframe,
  ContentFrameNextButton,
  LearnerAspectRatioFrame,
} from '@/components/courses/content-frame.styles';
import { CourseModuleFrame } from '@/components/courses/CourseModuleFrame';
import { ScormProvider } from '@/components/courses/scorm';
import { AppButton } from '@/ui/buttons';
import { OrientationWarning } from '@/ui/orientation-warning';
import { isMobile } from '@/utils/helpers/ui.helper';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledMobileNextButton = styled(AppButton)`
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  position: absolute;
  right: calc(var(--default-padding) * 3);
  top: var(--medium-padding);
  z-index: 999;
`;

const StyledSpinnerDiv = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: var(--white-main);
  padding: var(--default-padding);
  border-radius: var(--default-border-radius);
  opacity: 0.8;
  z-index: 9;
`;

const customCoursePropertyValueMap: Record<string, string | boolean> = {
  True: true,
  False: false,
};

type CourseScormModuleProps = {
  course: Course;
  attempt?: CourseAttempt;
  currentUser: User;
  scormDetails: LearnerCourseDispatchResponse;
  isContentStarted: boolean;
  isLastModule: boolean;
  isCompleted: boolean;
  isSubmittingStatus: boolean;
  setStatus: (status?: CourseStatusAttemptEnum) => void;
  setScore: (score?: number) => void;
  setIsSubmittingStatus: (isSubmittingStatus: boolean) => void;
  setIsBellowThreshold: (isBellowThreshold: boolean) => void;
  onNextModule: () => void;
};

export const CourseScormModule: React.FC<CourseScormModuleProps> = ({
  course,
  attempt,
  currentUser,
  scormDetails,
  isContentStarted,
  isLastModule,
  isCompleted,
  isSubmittingStatus,
  setStatus,
  setScore,
  setIsSubmittingStatus,
  setIsBellowThreshold,
  onNextModule,
}) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [frameLoaded, setFrameLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser?.firstName || !frameLoaded || !isContentStarted) return;

    let interval: any = null;

    const setUserData = () => {
      if (
        typeof (iframeRef.current?.contentWindow as any)?.GetPlayer ===
        'undefined'
      ) {
        return;
      }

      const player = (iframeRef.current?.contentWindow as any)?.GetPlayer();

      if (player) {
        player.SetVar('Firstname', currentUser?.firstName);
        player.SetVar('ITperson', currentUser?.email);
        player.SetVar('useremail', currentUser?.email);
        player.SetVar('usermanager', currentUser?.companyData?.manager ?? '');

        if (
          scormDetails?.properties &&
          Object.keys(scormDetails?.properties || {}).length > 0
        ) {
          Object.entries(scormDetails?.properties || {})
            .filter(([, v]) => !!scormDetails?.properties && !!v)
            .forEach(([k, v]) => {
              let value: string | boolean = v;

              if (
                Object.prototype.hasOwnProperty.call(
                  customCoursePropertyValueMap,
                  v,
                )
              ) {
                value = customCoursePropertyValueMap[v];
              }

              player.SetVar(k, value);
            });
        }

        if (interval) clearInterval(interval);
      }
    };

    interval = setInterval(setUserData, 200);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    frameLoaded,
    currentUser?.firstName,
    currentUser?.email,
    isContentStarted,
    scormDetails?.properties,
  ]);

  const showNextButton = isCompleted && !isLastModule;

  return (
    <CourseModuleFrame>
      <ScormProvider
        course={course}
        attempt={attempt}
        version={scormDetails.version as ScormVersion}
        user={currentUser}
        setStatus={setStatus}
        setScore={setScore}
        setIsSubmittingStatus={setIsSubmittingStatus}
        setIsBellowThreshold={setIsBellowThreshold}
        isPreview={false}
        isLastModule={isLastModule}
        setNextModule={onNextModule}
      >
        <LearnerAspectRatioFrame className={isMobile() ? 'mobile' : undefined}>
          {isSubmittingStatus && (
            <StyledSpinnerDiv>
              <ProgressSpinner />
              <span>{t('content.processing.results')}</span>
            </StyledSpinnerDiv>
          )}

          {frameLoaded && <OrientationWarning />}

          {isMobile() && showNextButton && (
            <StyledMobileNextButton
              className="next-button"
              size="xs"
              icon="pi pi-chevron-right"
              isDisabled={isSubmittingStatus}
              onClick={onNextModule}
            />
          )}

          <ContentFrameIframe
            key={scormDetails.url}
            src={scormDetails.url}
            ref={iframeRef}
            style={{
              visibility: isSubmittingStatus ? 'hidden' : 'visible',
            }}
            onLoad={() => setFrameLoaded(true)}
          />
          {showNextButton && !isMobile() && (
            <ContentFrameNextButton
              size="xs"
              icon="pi pi-chevron-right"
              isDisabled={isSubmittingStatus}
              onClick={onNextModule}
            />
          )}
        </LearnerAspectRatioFrame>
      </ScormProvider>
    </CourseModuleFrame>
  );
};
